import { useEffect, useState } from "react";
import { Form, Input, Skeleton, TextArea } from "../../components/ui";
import {
  useAddNotification,
  useGetNotificationById,
  useUpdateNotification,
} from "../../query/notificationQuery";
import { useGetAllTeachers } from "../../query/teacherQuery";
import { useGetAllParents } from "../../query/parentQuery";
import { useGetAllClasses } from "../../query/classQuery";
import swal from "../../utils/swal";
import { useNavigate, useParams } from "react-router-dom";
import { Bell, Megaphone } from "lucide-react";
import NotificationAudiencePicker, { NotificationTypePicker } from "./NotificationAudiencePicker";
import "../teachers/addTeacher.css";
import "./notifications.css";

function AddNotification() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [form] = Form.useForm();
  const [sendTo, setSendTo] = useState("ALL");
  const [notificationType, setNotificationType] = useState("GENERAL");
  const [selectedTeachers, setSelectedTeachers] = useState([]);
  const [selectedParents, setSelectedParents] = useState([]);
  const [selectedClassroom, setSelectedClassroom] = useState("");

  const { mutate: addNotification, isPending: isAdding } = useAddNotification();
  const { mutate: updateNotification, isPending: isUpdating } = useUpdateNotification();
  const { data: notification, isLoading } = useGetNotificationById(isEdit ? id : undefined);
  const { data: teachersData, isLoading: loadingTeachers } = useGetAllTeachers({
    page: 1,
    limit: 200,
    status: "ACTIVE",
  });
  const { data: parentsData, isLoading: loadingParents } = useGetAllParents({
    page: 1,
    limit: 200,
    status: "ACTIVE",
  });
  const { data: classesData, isLoading: loadingClasses } = useGetAllClasses({
    page: 1,
    limit: 200,
    status: "ACTIVE",
  });

  const teachers = teachersData?.docs || [];
  const parents = parentsData?.docs || [];
  const classrooms = classesData?.docs || [];
  const isMutating = isAdding || isUpdating;
  const loadingAudience = loadingTeachers || loadingParents || loadingClasses;

  useEffect(() => {
    if (!isEdit || !notification) return;
    form.setFieldsValue({
      title: notification.title || "",
      content: notification.content || "",
    });
    setNotificationType(notification.type || "GENERAL");
    setSendTo(notification.sendTo && notification.sendTo !== "ADMIN" ? notification.sendTo : "ALL");
    setSelectedTeachers((notification.targetTeachers || []).map(String));
    setSelectedParents((notification.targetParents || []).map(String));
    setSelectedClassroom(notification.targetClassroom?._id || notification.targetClassroom || "");
  }, [isEdit, notification, form]);

  const validateAudience = () => {
    if (sendTo === "CUSTOM" && !selectedTeachers.length && !selectedParents.length) {
      swal("Oops!", "Select at least one teacher or parent.", "error");
      return false;
    }
    if (sendTo === "CLASSROOM" && !selectedClassroom) {
      swal("Oops!", "Please select a class.", "error");
      return false;
    }
    return true;
  };

  const onFinish = (values) => {
    if (!validateAudience()) return;

    const payload = {
      title: values.title,
      content: values.content || "",
      type: notificationType,
      sendTo,
      teachers: selectedTeachers,
      parents: selectedParents,
      classroom: sendTo === "CLASSROOM" ? selectedClassroom : "",
    };

    if (isEdit) {
      updateNotification(
        { id, title: payload.title, content: payload.content, type: payload.type },
        {
          onSuccess: () => {
            swal("Success", "Notification updated successfully", "success");
            navigate(`/notifications/${id}`);
          },
          onError: (error) => swal("Error", error?.message || "Failed to update notification", "error"),
        }
      );
      return;
    }

    addNotification(payload, {
      onSuccess: () => {
        swal("Success", "Notice queued and will be delivered in the background.", "success");
        navigate("/notifications");
      },
      onError: (error) => swal("Error", error?.message || "Failed to create notification", "error"),
    });
  };

  if (isEdit && isLoading) {
    return (
      <div className="tf-page">
        <Skeleton active />
      </div>
    );
  }

  return (
    <div className="tf-page notif-form-page">
      <div className="notif-form-hero">
        <span className="notif-form-hero-icon">
          <Megaphone size={24} />
        </span>
        <div>
          <h1>{isEdit ? "Edit notice" : "Create notice"}</h1>
          <p>Write your message, choose who should receive it, and send it without blocking the admin portal.</p>
        </div>
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        autoComplete="off"
        initialValues={{ title: "", content: "" }}
      >
        <section className="tf-card">
          <h2 className="tf-card-head">Message</h2>
          <div className="tf-card-body">
            <div className="tf-field is-full">
              <p className="tf-label">
                Title <span>*</span>
              </p>
              <Form.Item
                name="title"
                className="tf-item mb-0"
                rules={[{ required: true, message: "Please enter title" }]}
              >
                <Input placeholder="School closure notice" />
              </Form.Item>
            </div>

            <div className="tf-field is-full">
              <p className="tf-label">Content</p>
              <Form.Item name="content" className="tf-item mb-0">
                <TextArea rows={6} placeholder="Write the full notice message..." />
              </Form.Item>
            </div>

            <div className="tf-field is-full">
              <p className="tf-label">
                Type <span>*</span>
              </p>
              <NotificationTypePicker value={notificationType} onChange={setNotificationType} />
            </div>
          </div>
        </section>

        <section className="tf-card">
          <h2 className="tf-card-head">Audience</h2>
          <div className="tf-card-body notif-form-audience-body">
            {loadingAudience && !isEdit ? (
              <Skeleton active paragraph={{ rows: 4 }} />
            ) : (
              <NotificationAudiencePicker
                sendTo={sendTo}
                onSendToChange={setSendTo}
                teachers={teachers}
                parents={parents}
                classrooms={classrooms}
                selectedTeachers={selectedTeachers}
                selectedParents={selectedParents}
                selectedClassroom={selectedClassroom}
                onTeachersChange={setSelectedTeachers}
                onParentsChange={setSelectedParents}
                onClassroomChange={setSelectedClassroom}
                disabled={isEdit}
              />
            )}
            {isEdit ? (
              <p className="notif-audience-hint">
                Audience cannot be changed after a notice has been created.
              </p>
            ) : null}
          </div>
        </section>

        <div className="notif-form-summary">
          <Bell size={18} />
          <span>
            {sendTo === "CUSTOM"
              ? `${selectedTeachers.length} teacher(s) and ${selectedParents.length} parent(s) selected.`
              : sendTo === "CLASSROOM"
              ? selectedClassroom
                ? `Sending to class recipients for ${
                    classrooms.find((item) => String(item._id) === String(selectedClassroom))
                      ?.classroomName || "selected class"
                  }.`
                : "Choose a class to see the recipient summary."
              : "This notice will be queued for delivery in the background."}
          </span>
        </div>

        <div className="tf-actions">
          <button type="button" className="tf-btn is-ghost" onClick={() => navigate("/notifications")}>
            Cancel
          </button>
          <button type="submit" className="tf-btn is-primary" disabled={isMutating || loadingAudience}>
            {isMutating ? "Saving..." : isEdit ? "Save changes" : "Send notice"}
          </button>
        </div>
      </Form>
    </div>
  );
}

export default AddNotification;
